import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Anchor,
  Breadcrumbs,
  Button,
  Center,
  Flex,
  Group,
  Text,
} from "@mantine/core";
import { IconInfoCircle, IconPlus } from "@tabler/icons-react";
import { GraphQLQuery, GraphQLSubscription } from "@aws-amplify/api";
import {
  FetchUserAttributesOutput,
  fetchUserAttributes,
} from "aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import BoxLoader from "@/components/BoxLoader";
import ConfirmDeleteTemplateModal from "@/components/Template/ConfirmDeleteTemplateModal";
import EditTemplateModal from "@/components/Template/EditTemplateModal";
import TemplateFormModal from "@/components/Template/TemplateForm/TemplateFormModal";
import TemplateTable from "@/components/Template/TemplateTable/TemplateTable";
import { sortByDate, sortByUpdatedAt } from "@/utils/utils";
import { templatesByLIDAndUpdatedAt } from "@/graphql/queries";
import {
  onCreateTemplate,
  onDeleteTemplate,
  onUpdateTemplate,
} from "@/graphql/subscriptions";
import {
  ModelSortDirection,
  OnCreateTemplateSubscription,
  OnCreateTemplateSubscriptionVariables,
  OnDeleteTemplateSubscription,
  OnDeleteTemplateSubscriptionVariables,
  OnUpdateTemplateSubscription,
  OnUpdateTemplateSubscriptionVariables,
  TemplatesByLIDAndUpdatedAtQuery,
  TemplatesByLIDAndUpdatedAtQueryVariables,
} from "@/API";

const client = generateClient();

function Templates() {
  const locationState = useLocation();
  const state = locationState.state;

  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState();

  const [isAddTemplateModalVisible, setIsAddTemplateModalVisible] =
    useState(false);
  const [isEditTemplateModalVisible, setIsEditTemplateModalVisible] =
    useState(false);
  const [modalConfirmDeleteOpened, setModalConfirmDeleteOpened] =
    useState(false);

  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (state?.addTemplate) {
      setIsAddTemplateModalVisible(true);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    setupSubscription();
  }, []);

  async function fetchTemplates() {
    setRefreshing(true);
    try {
      const userAttr: FetchUserAttributesOutput = await fetchUserAttributes();
      const templatesQueryInput: TemplatesByLIDAndUpdatedAtQueryVariables = {
        lID: userAttr["custom:location"]!,
        sortDirection: ModelSortDirection.DESC,
        limit: 1000,
        filter: {
          _deleted: { ne: true },
          archived: { ne: true },
        },
      };
      const templatesData: any = await client.graphql<
        GraphQLQuery<TemplatesByLIDAndUpdatedAtQuery>
      >({ query: templatesByLIDAndUpdatedAt, variables: templatesQueryInput });
      const templatesList =
        templatesData.data.templatesByLIDAndUpdatedAt!.items;
      const templatesSortUpdated = getMaxUpdatedTemplate(templatesList);
      setTemplates(templatesSortUpdated.sort(sortByUpdatedAt));
      
      // Trigger template migration to Supabase when viewing template list
      if (templatesSortUpdated.length > 0) {
        console.log(`[TEMPLATE-MIGRATION] Found ${templatesSortUpdated.length} templates to migrate to Supabase`);
        
        // Migrate templates in background (don't await to avoid blocking UI)
        setTimeout(async () => {
          try {
            const { DatabaseMigrationAdapter } = await import("@/services/migration");
            const migrationAdapter = DatabaseMigrationAdapter.getInstance();
            
            // Batch migrate templates
            for (let i = 0; i < templatesSortUpdated.length; i++) {
              const template = templatesSortUpdated[i];
              try {
                console.log(`[TEMPLATE-MIGRATION] Migrating template ${i + 1}/${templatesSortUpdated.length}:`, template.name);
                await migrationAdapter.handleTemplateOperation('CREATE', template as Record<string, unknown>, { data: { createTemplate: template } } as Record<string, unknown>);
                console.log(`[TEMPLATE-MIGRATION] Successfully migrated template:`, template.name);
              } catch (templateError) {
                console.warn(`[TEMPLATE-MIGRATION] Failed to migrate template ${template.name}:`, templateError);
              }
              
              // Small delay between templates to prevent overwhelming
              if (i < templatesSortUpdated.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 250));
              }
            }
            console.log(`[TEMPLATE-MIGRATION] Batch template migration completed`);
          } catch (error) {
            console.warn('[TEMPLATE-MIGRATION] Migration adapter failed:', error);
          }
        }, 1300); // Start after 1.3 seconds
      }
    } catch (err) {
      console.warn(err);
    }
    setRefreshing(false);
  }

  const getMaxUpdatedTemplate = (templates: any) => {
    return templates?.map((template: any) => {
      const lstDate = [template.updatedAt];

      template?.sections?.items?.map((section: any) => {
        lstDate.push(section.updatedAt);
      });
      template?.subsections?.items?.map((subsection: any) => {
        lstDate.push(subsection.updatedAt);
      });
      template?.fields?.items?.map((field: any) => {
        lstDate.push(field.updatedAt);
      });
      template?.textboxes?.items?.map((textbox: any) => {
        lstDate.push(textbox.updatedAt);
      });
      const dateSorted = lstDate.sort(sortByDate);
      template.updatedAt = dateSorted[0];
      return template;
    });
  };

  async function setupSubscription() {
    const userAttr: FetchUserAttributesOutput = await fetchUserAttributes();
    const varsCreateTemplate: OnCreateTemplateSubscriptionVariables = {
      filter: {
        lID: { eq: userAttr["custom:location"] },
      },
    };
    const createSub = client
      .graphql<GraphQLSubscription<OnCreateTemplateSubscription>>({
        query: onCreateTemplate,
        variables: varsCreateTemplate,
      })
      .subscribe({
        next: async () => {
          fetchTemplates();
        },
        error: (error) => console.warn(error),
      });

    const varsUpdateTemplate: OnUpdateTemplateSubscriptionVariables = {
      filter: {
        lID: { eq: userAttr["custom:location"] },
      },
    };
    const updateSub = client
      .graphql<GraphQLSubscription<OnUpdateTemplateSubscription>>({
        query: onUpdateTemplate,
        variables: varsUpdateTemplate,
      })
      .subscribe({
        next: async () => {
          fetchTemplates();
        },
        error: (error) => console.warn(error),
      });

    const varsDeleteTemplate: OnDeleteTemplateSubscriptionVariables = {
      filter: {
        lID: { eq: userAttr["custom:location"] },
      },
    };
    const deleteSub = client
      .graphql<GraphQLSubscription<OnDeleteTemplateSubscription>>({
        query: onDeleteTemplate,
        variables: varsDeleteTemplate,
      })
      .subscribe({
        next: () => {
          fetchTemplates();
        },
        error: (error) => console.warn(error),
      });
    return () => {
      createSub.unsubscribe();
      updateSub.unsubscribe();
      deleteSub.unsubscribe();
    };
  }

  return (
    <Group
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex",
        width: "100%",
        maxHeight: "100%",
        padding: 15,
      }}
    >
      <Group
        className="header-patients"
        flex={1}
        align="center"
        w={"100%"}
        justify="space-between"
      >
        <Flex align={"center"}>
          <Breadcrumbs separatorMargin={3} mt={0} separator="→">
            <NavLink to={""} className={"active"}>
              Templates
            </NavLink>
          </Breadcrumbs>
          <Anchor
            ml={10}
            href="https://vetscribe.notion.site/VetScribe-Templates-Unlimited-Customization-for-Your-Clinic-a7bbbe772e0344099cf27dfc7781d27c"
            target="_blank"
          >
            <IconInfoCircle width={20} />
          </Anchor>
        </Flex>
        <Group className="header-patients-action">
          <Button
            radius={20}
            onClick={() => setIsAddTemplateModalVisible(true)}
            fw={400}
          >
            <IconPlus width={16} />
            <Text fw={400} fz={14}>
              Add new template
            </Text>
          </Button>
        </Group>
      </Group>
      <Flex
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          width: "100%",
          minHeight: "calc(100vh - 300px)",
        }}
      >
        {refreshing ? (
          <BoxLoader />
        ) : templates.length == 0 ? (
          <Center pos={"relative"}>
            <Flex direction={"column"} justify={"center"} ta={"center"}>
              <p>No templates found</p>
              <p>Please add some templates</p>
              <Button
                radius={20}
                onClick={() => setIsAddTemplateModalVisible(true)}
                fw={400}
              >
                <IconPlus width={16} />
                <Text fw={400} fz={14}>
                  Add new template
                </Text>
              </Button>
            </Flex>
          </Center>
        ) : (
          <Group
            style={{
              width: "100%",
              display: "flex",
              flex: "1",
              alignItems: "flex-start",
              position: "relative",
              justifyContent: "center",
            }}
          >
            <TemplateTable data={templates} fetchTemplates={fetchTemplates} />
          </Group>
        )}
      </Flex>
      <EditTemplateModal
        modalOpened={isEditTemplateModalVisible}
        setModalOpened={setIsEditTemplateModalVisible}
        template={template}
        fetchTemplates={fetchTemplates}
        setTemplate={setTemplate}
        setModalConfirmDeleteOpened={setModalConfirmDeleteOpened}
      />
      <ConfirmDeleteTemplateModal
        modalOpened={modalConfirmDeleteOpened}
        setModalOpened={setModalConfirmDeleteOpened}
        template={template}
        fetchTemplates={fetchTemplates}
      />
      <TemplateFormModal
        opened={isAddTemplateModalVisible}
        onClose={() => setIsAddTemplateModalVisible(false)}
        fetchTemplates={fetchTemplates}
      />
    </Group>
  );
}

export default Templates;
